import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RedoIcon from '@mui/icons-material/Redo'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import DisabledReason from '../system/DisabledReason'
import { INNER_RADIUS, PILL_RADIUS } from '../../agent/components/surface'
import { POINT_STATUS_LABELS, isPointDone, pointStatus } from '../../utils/guided'
import type { GuidePoint, GuidePointState, GuidePointStatus } from '../../types/gotypes'

export interface GuideTrackerProps {
  points: GuidePoint[]
  states: GuidePointState[]
  /** Why the person may not steer the conversation (read-only); undefined when they may. */
  disabledReason?: string
  /** A point was clicked: the page puts "Can we cover … now?" in the composer. */
  onCoverPoint: (point: GuidePoint) => void
  /** Skip for now: the page puts the person's request in the composer. */
  onSkipPoint: (point: GuidePoint) => void
}

/** One point's mark: ticked, talking now, skipped, left out, or still to come. */
export function PointMark({ status, size = 16 }: { status: GuidePointStatus; size?: number }) {
  if (status === 'covered') return <CheckCircleIcon aria-hidden data-mark="covered" sx={{ fontSize: size, color: 'success.main' }} />
  if (status === 'skipped') return <RedoIcon aria-hidden data-mark="skipped" sx={{ fontSize: size, color: 'warning.main' }} />
  if (status === 'declined') return <RemoveCircleOutlineIcon aria-hidden data-mark="declined" sx={{ fontSize: size, color: 'text.disabled' }} />
  const dot = Math.round(size * 0.6)
  return (
    <Box
      component="span"
      aria-hidden
      data-mark={status}
      sx={{
        width: dot,
        height: dot,
        m: `${(size - dot) / 2}px`,
        borderRadius: '50%',
        boxSizing: 'border-box',
        flexShrink: 0,
        ...(status === 'active'
          ? {
              backgroundColor: 'info.main',
              animation: 'guidePointPulse 1.4s ease-in-out infinite',
              '@keyframes guidePointPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }
          : { border: '1.5px solid', borderColor: 'text.secondary', opacity: 0.6 }),
      }}
    />
  )
}

const SMALL_BUTTON_SX = { textTransform: 'none', fontSize: 12, borderRadius: PILL_RADIUS, px: 1.25, minWidth: 0, py: 0.25 } as const

/**
 * The agenda, with each point ticked off as the agent covers it. The page
 * refetches the response when a guide tool call ends, so a point changes
 * here without a reload. Clicking a point that is not done offers it to the
 * agent through the composer; nothing is sent until the person sends.
 */
const GuideTracker: React.FC<GuideTrackerProps> = ({ points, states, disabledReason, onCoverPoint, onSkipPoint }) => (
  <Box component="ol" data-testid="guide-tracker" aria-label="Agenda" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    {points.map((point, i) => {
      const status = pointStatus(states, point.id)
      const state = states.find((s) => s.point_id === point.id)
      const done = isPointDone(status)
      const clickable = !done && !disabledReason
      const label = `${i + 1}. ${point.title}`
      const row = (
        <>
          <PointMark status={status} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: status === 'active' ? 600 : 500,
                color: done ? 'text.secondary' : 'text.primary',
                lineHeight: 1.35,
              }}
            >
              {label}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.35 }}>{POINT_STATUS_LABELS[status]}</Typography>
          </Box>
        </>
      )
      return (
        <Box component="li" key={point.id} data-testid={`guide-point-${point.id}`} data-status={status}>
          {clickable ? (
            <Box
              component="button"
              type="button"
              onClick={() => onCoverPoint(point)}
              aria-label={`${label}: ${POINT_STATUS_LABELS[status]}. Ask to cover it now`}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: INNER_RADIUS,
                cursor: 'pointer',
                backgroundColor: status === 'active' ? 'action.selected' : 'transparent',
                '&:hover': { backgroundColor: 'action.hover' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'info.main', outlineOffset: 1 },
              }}
            >
              {row}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1, py: 0.75, borderRadius: INNER_RADIUS, backgroundColor: status === 'active' ? 'action.selected' : 'transparent' }}>
              {row}
            </Box>
          )}
          {status === 'active' && (
            <Box sx={{ pl: 4, pr: 1, pb: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {state && state.missing.length > 0 && (
                <Typography data-testid="guide-point-missing" sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.4 }}>
                  Still to cover: {state.missing.join('; ')}
                </Typography>
              )}
              <Box>
                <DisabledReason reason={disabledReason}>
                  <Button size="small" color="info" onClick={() => onSkipPoint(point)} sx={SMALL_BUTTON_SX}>
                    Skip for now
                  </Button>
                </DisabledReason>
              </Box>
            </Box>
          )}
        </Box>
      )
    })}
  </Box>
)

export default GuideTracker
